import type { FieldNamesMarkedBoolean } from 'react-hook-form';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import type { AgentInfo, UpdateAgentData } from '@shared/api';

export const createEmptyStage = (order: number) => ({
  order,
  agent_field_id: 0,
  prompt: '',
});

export const createEmptyAgentLink = () => ({
  target_agent_id: 0,
  prompt: '',
});

export const normalizePromptFormValues = (
  values?: Partial<UpdateFormValues> | null,
): UpdateFormValues => ({
  name: values?.name ?? '',
  role: values?.role ?? '',
  style: values?.style ?? '',
  recommendations: values?.recommendations ?? '',
  charge_method: values?.charge_method ?? 'lead',
  stages: (values?.stages ?? []).map((stage, index) => ({
    order: Number(stage?.order ?? index),
    agent_field_id: Number(stage?.agent_field_id ?? 0),
    prompt: stage?.prompt ?? '',
  })),
  agent_links: (values?.agent_links ?? []).map((link) => ({
    target_agent_id: Number(link?.target_agent_id ?? 0),
    prompt: link?.prompt ?? '',
  })),
  show_form_prompts: (values?.show_form_prompts ?? []).map((prompt) => prompt ?? ''),
  rules: (values?.rules ?? []).length ? (values?.rules ?? []).map((rule) => rule ?? '') : [''],
  exceptions: (values?.exceptions ?? []).length
    ? (values?.exceptions ?? []).map((exception) => exception ?? '')
    : [''],
});

export const mapAgentInfoToFormValues = (agent: AgentInfo): UpdateFormValues =>
  normalizePromptFormValues({
    name: agent.name || '',
    role: agent.role || '',
    style: agent.style || '',
    recommendations: agent.recommendations ?? '',
    charge_method: agent.charge_method ?? 'lead',
    stages: (agent.agent_stages ?? [])
      .slice()
      .sort((firstStage, secondStage) => firstStage.order - secondStage.order)
      .map((stage, index) => ({
        order: index,
        agent_field_id: stage.agent_field_id,
        prompt: stage.prompt,
      })),
    agent_links: (agent.agent_links ?? []).map((link) => ({
      target_agent_id: link.target_agent_id,
      prompt: link.prompt,
    })),
    show_form_prompts: (agent.show_form_prompts ?? []).map((prompt) => prompt ?? ''),
    rules: agent.rules ?? [],
    exceptions: agent.exceptions ?? [],
  });

export const hasDirty = (v: unknown): boolean => {
  if (v === true) return true;
  if (!v) return false;
  if (Array.isArray(v)) return v.some(hasDirty);
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).some(hasDirty);
  return false;
};

export const buildUpdateAgentPatch = (
  values: UpdateFormValues,
  dirty: FieldNamesMarkedBoolean<UpdateFormValues>,
): UpdateAgentData => {
  const patch: UpdateAgentData = {};

  if (dirty.name) patch.name = values.name;
  if (dirty.role) patch.role = values.role;
  if (dirty.style) patch.style = values.style;
  if (dirty.recommendations) patch.recommendations = values.recommendations;
  if (dirty.charge_method) patch.charge_method = values.charge_method;

  if (hasDirty(dirty.rules)) patch.rules = values.rules;
  if (hasDirty(dirty.exceptions)) patch.exceptions = values.exceptions;
  if (hasDirty(dirty.show_form_prompts)) patch.show_form_prompts = values.show_form_prompts;

  if (hasDirty(dirty.stages)) {
    patch.stages = (values.stages ?? []).map((stage, index) => ({
      order: index,
      agent_field_id: stage.agent_field_id,
      prompt: stage.prompt,
    }));
  }

  if (hasDirty(dirty.agent_links)) {
    patch.agent_links = (values.agent_links ?? []).map((link) => ({
      target_agent_id: link.target_agent_id,
      prompt: link.prompt,
    }));
  }

  return patch;
};
